import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, X, Trash2, Pencil, BellRing, CheckCircle2, AlarmClock,
  Briefcase, CalendarDays, Wallet, PhoneCall, ClipboardCheck, ReceiptText,
  Repeat, Users as UsersIcon, Lock, BellOff, CheckCheck,
} from "lucide-react";

const CATEGORIES = [
  { key: "work",          label: "Work",          icon: Briefcase,      badge: "bg-blue-500/10 text-blue-500" },
  { key: "meeting",       label: "Meeting",       icon: CalendarDays,   badge: "bg-violet-500/10 text-violet-500" },
  { key: "local_payment", label: "Local Payment", icon: Wallet,         badge: "bg-emerald-500/10 text-emerald-500" },
  { key: "urgent_call",   label: "Urgent Call",   icon: PhoneCall,      badge: "bg-red-500/10 text-red-500" },
  { key: "work_done",     label: "Work Done",     icon: ClipboardCheck, badge: "bg-teal-500/10 text-teal-500" },
  { key: "pending_bill",  label: "Pending Bill",  icon: ReceiptText,    badge: "bg-amber-500/10 text-amber-600" },
];
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
const RECURRENCES = ["once", "daily", "weekly", "monthly"];

function fmtDue(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
const isOverdue = (r) => r.status === "pending" && new Date(r.due_at) < new Date();
const isToday = (r) => {
  const d = new Date(r.due_at); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

export default function Reminders() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (catFilter) params.set("category", catFilter);
      if (statusFilter) params.set("status", statusFilter);
      const qs = params.toString();
      const [r, u] = await Promise.all([
        api.get(`/reminders${qs ? `?${qs}` : ""}`),
        api.get("/users"),
      ]);
      setItems(r.data);
      setUsers(u.data.filter((x) => x.role !== "customer"));
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [catFilter, statusFilter]);

  const tiles = useMemo(() => {
    const pending = items.filter((r) => r.status === "pending");
    return [
      { key: "pending", label: "Pending", value: statusFilter === "done" ? "—" : pending.length, icon: BellRing },
      { key: "overdue", label: "Overdue", value: pending.filter(isOverdue).length, icon: AlarmClock, danger: true },
      { key: "today",   label: "Due Today", value: pending.filter((r) => isToday(r) && !isOverdue(r)).length, icon: CalendarDays },
      { key: "done",    label: "Done", value: items.filter((r) => r.status === "done").length, icon: CheckCircle2 },
    ];
  }, [items, statusFilter]);

  const markDone = async (r) => {
    try { await api.post(`/reminders/${r.id}/done`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const toggleSilence = async (r) => {
    try { await api.patch(`/reminders/${r.id}`, { silenced: !r.silenced }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const closeReminder = async (r) => {
    if (r.recurrence !== "once" && !window.confirm(`Close "${r.title}" permanently? It will stop repeating and move to Done.`)) return;
    try { await api.post(`/reminders/${r.id}/close`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete this reminder?")) return;
    try { await api.delete(`/reminders/${r.id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Stay on top</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Reminders</h1>
          <p className="mt-1 text-sm text-muted-foreground">Work, meetings, payments, urgent calls, completed work and pending bills.</p>
        </div>
        <button data-testid="reminder-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> New reminder
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.key} data-testid={`reminder-tile-${t.key}`} className="sharp-card p-4">
            <div className={`flex h-9 w-9 items-center justify-center rounded-md ${t.danger ? "bg-red-500/10 text-red-500" : "bg-primary/10 text-primary"}`}>
              <t.icon className="h-5 w-5" />
            </div>
            <p className="label-eyebrow mt-3">{t.label}</p>
            <p className={`mt-1 font-heading text-2xl font-bold ${t.danger && t.value > 0 ? "text-red-500" : ""}`}>{t.value}</p>
          </div>
        ))}
      </section>

      <div className="mb-3 flex flex-wrap gap-2">
        <button data-testid="reminder-filter-all" onClick={() => setCatFilter("")}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${!catFilter ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c.key} data-testid={`reminder-filter-${c.key}`} onClick={() => setCatFilter(c.key)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${catFilter === c.key ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
            <c.icon className="h-3.5 w-3.5" /> {c.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {["pending", "done", ""].map((s) => (
            <button key={s || "any"} data-testid={`reminder-status-${s || "any"}`} onClick={() => setStatusFilter(s)}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium capitalize ${statusFilter === s ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
              {s || "Any status"}
            </button>
          ))}
        </div>
      </div>

      <div className="sharp-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="px-4 py-2 label-eyebrow">Due</th>
              <th className="px-4 py-2 label-eyebrow">Title</th>
              <th className="px-4 py-2 label-eyebrow">Category</th>
              <th className="px-4 py-2 label-eyebrow">Repeat</th>
              <th className="px-4 py-2 label-eyebrow">Assigned To</th>
              <th className="px-4 py-2 label-eyebrow">Visibility</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const c = CAT[r.category] || CAT.work;
              const overdue = isOverdue(r);
              return (
                <tr key={r.id} data-testid={`reminder-row-${r.id}`} className={`border-b border-border/40 hover:bg-secondary/40 ${overdue ? "bg-red-500/5" : ""}`}>
                  <td className={`whitespace-nowrap px-4 py-2 ${overdue ? "font-semibold text-red-500" : "text-muted-foreground"}`}>
                    {fmtDue(r.due_at)}
                    {overdue && <span className="ml-2 rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-500">Overdue</span>}
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-medium">{r.title}</p>
                    {r.notes && <p className="max-w-xs truncate text-xs text-muted-foreground">{r.notes}</p>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${c.badge}`}>
                      <c.icon className="h-3 w-3" /> {c.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 capitalize text-muted-foreground">
                    {r.recurrence !== "once" ? (
                      <span className="inline-flex items-center gap-1"><Repeat className="h-3.5 w-3.5" />{r.recurrence}</span>
                    ) : "Once"}
                  </td>
                  <td className="px-4 py-2">{r.assigned_to_name || <span className="text-muted-foreground">Me ({r.created_by_name || "—"})</span>}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1 text-xs capitalize text-muted-foreground">
                      {r.visibility === "shared" ? <UsersIcon className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                      {r.visibility}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${r.status === "done" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-500"}`}>{r.status}</span>
                    {r.silenced && r.status === "pending" && (
                      <span className="ml-1 inline-flex items-center gap-0.5 rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600"><BellOff className="h-2.5 w-2.5" /> muted</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {r.status === "pending" && (
                        <button data-testid={`reminder-done-${r.id}`} title={r.recurrence !== "once" ? "Done — moves to next occurrence" : "Mark done"}
                          onClick={() => markDone(r)}
                          className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /></button>
                      )}
                      {r.status === "pending" && r.recurrence !== "once" && (
                        <button data-testid={`reminder-close-${r.id}`}
                          title="Close — work complete, stop repeating"
                          onClick={() => closeReminder(r)}
                          className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-teal-500">
                          <CheckCheck className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {r.status === "pending" && (
                        <button data-testid={`reminder-silence-${r.id}`}
                          title={r.silenced ? "Un-silence — popups will appear again" : "Silence — stop popups for this reminder"}
                          onClick={() => toggleSilence(r)}
                          className={`rounded-md border border-border/70 p-1.5 ${r.silenced ? "text-amber-500 hover:text-muted-foreground" : "text-muted-foreground hover:text-amber-500"}`}>
                          {r.silenced ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      <button data-testid={`reminder-edit-${r.id}`} onClick={() => setEditing(r)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`reminder-delete-${r.id}`} onClick={() => del(r)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-muted-foreground">No reminders here. Create one with &ldquo;New reminder&rdquo;.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <ReminderModal users={users} me={user} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ReminderModal existing={editing} users={users} me={user} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ReminderModal({ users, me, onClose, onSaved, existing }) {
  const defaultDue = () => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [form, setForm] = useState({
    title: existing?.title || "",
    category: existing?.category || "work",
    due_at: existing?.due_at?.slice(0, 16) || defaultDue(),
    recurrence: existing?.recurrence || "once",
    visibility: existing?.visibility || "private",
    assigned_to: existing?.assigned_to || "",
    notes: existing?.notes || "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (existing) await api.patch(`/reminders/${existing.id}`, payload);
      else await api.post("/reminders", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? "Edit reminder" : "New reminder"}</h3>
          <button data-testid="reminder-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Title *</label>
            <input data-testid="reminder-form-title" required value={form.title} onChange={set("title")} placeholder="e.g. Collect payment from ABC Corp" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Category *</label>
            <select data-testid="reminder-form-category" required value={form.category} onChange={set("category")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Due date & time *</label>
            <input data-testid="reminder-form-due" required type="datetime-local" value={form.due_at} onChange={set("due_at")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Repeat</label>
            <select data-testid="reminder-form-recurrence" value={form.recurrence} onChange={set("recurrence")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize">
              {RECURRENCES.map((r) => <option key={r} value={r}>{r === "once" ? "Once (no repeat)" : r}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Visibility</label>
            <select data-testid="reminder-form-visibility" value={form.visibility} onChange={set("visibility")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="private">Private (me + assignee)</option>
              <option value="shared">Shared (all staff)</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Assign to</label>
            <select data-testid="reminder-form-assignee" value={form.assigned_to} onChange={set("assigned_to")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Me —</option>
              {users.filter((u) => u.id !== me?.id).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g, " ")})</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Notes</label>
            <textarea data-testid="reminder-form-notes" value={form.notes} onChange={set("notes")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="reminder-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create reminder")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
